/**
 * KESTREL — the counter-striker. Move table.
 *
 * Discipline: *Vel Sarra*, an invented hybrid of low-line kicking and close-quarters trapping.
 * 1.72 m, 62 kg. See src/combat/roster.js for stats and ARCHITECTURE.md §16 for the contract.
 *
 * DESIGN INTENT
 * -------------
 * Kestrel wins by making the opponent commit and then taking the frames back. Every part of the
 * frame data below serves that:
 *
 *  - The fastest tools in the game (i10 jab, i11 low) but the SHORTEST reach on them. Kestrel
 *    has to be in range to use them, and being in range is where Kestrel is weakest.
 *  - Excellent whiff punishment: `ky-veinsplit` and `ky-readcut` are fast, long, and only pay off
 *    against a recovering opponent.
 *  - Nothing is plus on block that is also fast or long. The only genuinely plus-on-block move
 *    (`ky-flow-check`, +1) is 0.55 m of reach and requires Flow.
 *  - Modest per-hit damage. Kestrel's damage comes from *frequency of correct reads*, not from
 *    any single route. No Kestrel combo reaches Bulwark's throw damage.
 *  - Deliberate hole: no fast panic button. Kestrel's fastest reversal is the i5 parry, which is a
 *    read, not a mash. Against sustained close-range pressure Kestrel must guess.
 *
 * FLOW STANCE + READ METER
 * ------------------------
 * `ky-flow` enters Flow. In Flow, Kestrel walks 22 % faster, gains six upgraded moves, and loses
 * 20 % guard integrity — pressure hurts more while you are hunting. Read builds from counter-hits
 * (+18), parries (+25) and whiff punishes (+14) and is spent on `ky-readcut` (40) and the
 * `ky-severance` finisher (85).
 *
 * Several Flow moves deliberately SHARE an input with their neutral counterpart (`1`, `3`, `f+2`).
 * `findMove` resolves this correctly and deterministically: out of Flow the `stanceRequired`
 * variant is filtered out; in Flow both match at equal specificity and the command table's
 * secondary sort (ascending startup) puts the faster Flow version first, which then wins because
 * a later equal-specificity match cannot displace it. That is the intended behaviour — entering
 * Flow changes what your buttons do.
 *
 * Frame data is authored in 60 Hz frames. `hitAdvantage`/`blockAdvantage` are DERIVED by the
 * schema as `stun - (active + recovery - 1)`; the intended values are in comments so a future
 * edit that breaks one is obvious.
 */

/** Compact hitbox constructor. Fighter local space, metres: +X forward, +Y up from the ground. */
const hb = (from, to, joint, ox, oy, ex, ey, r, extra = {}) => ({
  from, to, joint, ox, oy, oz: 0, ex, ey, ez: 0, r, ...extra,
});

export const KESTREL_MOVES = [
  // ===========================================================================
  // JABS AND STRINGS — Kestrel's fastest, shortest tools. The pressure engine.
  // ===========================================================================
  {
    id: 'ky-jab', name: 'Jab', input: '1', category: 'strike',
    startup: 10, active: 2, recovery: 9,            // blk -1, hit +6
    level: 'mid', damage: 9, hitstun: 16, blockstun: 9,
    pushbackHit: 0.10, pushbackBlock: 0.16, selfPushbackBlock: 0.06,
    cancelWindow: [11, 21], cancels: ['ky-jab2', 'ky-lowcut'], delayableUntil: 5,
    hitboxes: [hb(11, 12, 'handL', 0.28, 1.38, 0.62, 1.37, 0.10, { tracking: 0.6, priority: 2 })],
    counterHit: { damageMul: 1.2, hitstunBonus: 6, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 1.0, cost: 0.08, minScale: 0.22 },
    meterGain: { hit: 4, block: 2, whiff: 1 },
    tracking: 0.6, anim: 'ky.jab1',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: 'arc-thin' },
    tags: ['jab', 'string-starter', 'counter-starter'],
    aiHints: { range: 0.72, risk: 0.08, use: ['pressure', 'interrupt', 'poke'] },
  },
  {
    id: 'ky-jab2', name: 'Jab, Cross', input: '1', category: 'strike',
    followUpOf: 'ky-jab',
    startup: 8, active: 2, recovery: 11,            // blk -3, hit +5
    level: 'mid', damage: 8, hitstun: 17, blockstun: 9,
    pushbackHit: 0.12, pushbackBlock: 0.18,
    cancelWindow: [10, 21], cancels: ['ky-jab3', 'ky-hook'], delayableUntil: 6,
    hitboxes: [hb(9, 10, 'handR', 0.30, 1.36, 0.66, 1.35, 0.10, { tracking: 0.55, priority: 2 })],
    counterHit: { damageMul: 1.2, hitstunBonus: 6, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.95, cost: 0.09, minScale: 0.22 },
    anim: 'ky.jab2',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: 'arc-thin' },
    tags: ['string', 'delayable'],
    aiHints: { range: 0.76, risk: 0.14, use: ['pressure'] },
  },
  {
    id: 'ky-jab3', name: 'Jab, Cross, Cutting Third', input: '2', category: 'strike',
    followUpOf: 'ky-jab2',
    startup: 12, active: 3, recovery: 18,           // blk -10, hit +2
    level: 'mid', damage: 16, hitstun: 22, blockstun: 10,
    pushbackHit: 0.22, pushbackBlock: 0.30, selfPushbackBlock: 0.14,
    hitboxes: [hb(13, 15, 'handR', 0.26, 1.32, 0.78, 1.40, 0.12, { tracking: 0.45, priority: 3 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 9, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'none', wallSplat: true },
    scaling: { starterValue: 0.85, cost: 0.12, minScale: 0.22 },
    anim: 'ky.jab3',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['string-ender', 'wall'],
    aiHints: { range: 0.90, risk: 0.5, use: ['pressure', 'wall'] },
  },
  {
    id: 'ky-lowcut', name: 'Jab, Low Cut', input: '3', category: 'strike',
    followUpOf: 'ky-jab',
    startup: 13, active: 2, recovery: 19,           // blk -13, hit -6
    level: 'low', damage: 12, hitstun: 14, blockstun: 7,
    pushbackHit: 0.14, pushbackBlock: 0.24,
    hitsAir: false,
    hitboxes: [hb(14, 15, 'footR', 0.24, 0.20, 0.70, 0.16, 0.11, { tracking: 0.5, priority: 2 })],
    counterHit: { damageMul: 1.25, hitstunBonus: 8, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.9, cost: 0.10, minScale: 0.22 },
    anim: 'ky.lowcut',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['string-ender', 'low', 'mixup'],
    aiHints: { range: 0.81, risk: 0.55, use: ['mixup', 'pressure'] },
  },

  // ===========================================================================
  // STANDING MIDS — the neutral game.
  // ===========================================================================
  {
    id: 'ky-straight', name: 'Straight', input: '2', category: 'strike',
    startup: 13, active: 2, recovery: 16,           // blk -9, hit +2
    level: 'mid', damage: 15, hitstun: 19, blockstun: 8,
    pushbackHit: 0.18, pushbackBlock: 0.26,
    cancelWindow: [16, 31], cancels: ['ky-hook'],
    hitboxes: [hb(14, 15, 'handR', 0.34, 1.35, 0.86, 1.34, 0.11, { tracking: 0.5, priority: 2 })],
    counterHit: { damageMul: 1.28, hitstunBonus: 8, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.95, cost: 0.10, minScale: 0.22 },
    anim: 'ky.straight',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-thin' },
    tags: ['poke', 'counter-starter'],
    aiHints: { range: 0.97, risk: 0.32, use: ['poke', 'punish'] },
  },
  {
    id: 'ky-elbow', name: 'Slip Elbow', input: 'df+2', category: 'strike',
    startup: 12, active: 2, recovery: 14,           // blk -6, hit +3
    level: 'mid', damage: 14, hitstun: 18, blockstun: 9,
    pushbackHit: 0.12, pushbackBlock: 0.18, selfPushbackBlock: 0.06,
    moveForward: 0.22,
    hitboxes: [hb(13, 14, 'elbowR', 0.22, 1.28, 0.58, 1.30, 0.13, { tracking: 0.7, priority: 3 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 10, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.92, cost: 0.09, minScale: 0.22 },
    tracking: 0.7, anim: 'ky.elbow',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: '' },
    tags: ['counter-starter', 'anti-sidestep'],
    aiHints: { range: 0.71, risk: 0.24, use: ['pressure', 'interrupt'] },
  },
  {
    id: 'ky-backfist', name: 'Spin Backfist', input: 'b+2', category: 'strike',
    startup: 18, active: 3, recovery: 20,           // blk -12, hit +2
    level: 'high', damage: 20, hitstun: 24, blockstun: 10,
    pushbackHit: 0.30, pushbackBlock: 0.34,
    hitboxes: [hb(19, 21, 'handR', 0.18, 1.48, 0.82, 1.46, 0.13, { tracking: 0.85, priority: 3 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'none', wallSplat: true },
    scaling: { starterValue: 0.85, cost: 0.12, minScale: 0.22 },
    tracking: 0.85, anim: 'ky.backfist',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['anti-sidestep', 'wall', 'counter-starter', 'duckable'],
    aiHints: { range: 0.95, risk: 0.62, use: ['whiff-punish', 'wall'] },
  },
  {
    id: 'ky-midkick', name: 'Round Kick', input: '4', category: 'strike',
    startup: 14, active: 3, recovery: 18,           // blk -12, hit 0
    level: 'mid', damage: 19, hitstun: 20, blockstun: 8,
    pushbackHit: 0.24, pushbackBlock: 0.32,
    hitboxes: [hb(15, 17, 'footR', 0.32, 1.14, 1.02, 1.20, 0.13, { tracking: 0.5, priority: 3 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.9, cost: 0.11, minScale: 0.22 },
    anim: 'ky.midkick',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['poke', 'range'],
    aiHints: { range: 1.15, risk: 0.45, use: ['poke', 'range'] },
  },
  {
    id: 'ky-highkick', name: 'Arc Kick', input: 'f+4', category: 'strike',
    startup: 17, active: 3, recovery: 20,           // blk -13, hit +2
    level: 'high', damage: 22, hitstun: 24, blockstun: 9,
    pushbackHit: 0.30, pushbackBlock: 0.36,
    hitboxes: [hb(18, 20, 'footR', 0.34, 1.52, 1.06, 1.58, 0.13, { tracking: 0.4, priority: 3 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'soft' },
    scaling: { starterValue: 0.82, cost: 0.13, minScale: 0.22 },
    anim: 'ky.highkick',
    sfx: { swing: 'swing-whoosh', hit: 'impact-head', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['duckable', 'knockdown', 'range'],
    aiHints: { range: 1.22, risk: 0.6, use: ['whiff-punish', 'range'] },
  },
  {
    id: 'ky-crescent', name: 'Crescent Sweep', input: 'b+4', category: 'strike',
    startup: 20, active: 4, recovery: 22,           // blk -15, hit +3
    level: 'mid', damage: 24, hitstun: 28, blockstun: 10,
    pushbackHit: 0.34, pushbackBlock: 0.38,
    hitboxes: [hb(21, 24, 'footL', 0.20, 1.05, 1.10, 1.44, 0.14, { tracking: 0.95, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { knockdown: 'soft', wallSplat: true },
    scaling: { starterValue: 0.8, cost: 0.14, minScale: 0.22 },
    tracking: 0.95, anim: 'ky.crescent',
    sfx: { swing: 'swing-whoosh', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['anti-sidestep', 'wall', 'knockdown'],
    aiHints: { range: 1.24, risk: 0.72, use: ['whiff-punish', 'anti-air', 'wall'] },
  },

  // ===========================================================================
  // LOWS — Kestrel's low-line game is the reason the discipline is named for it.
  // ===========================================================================
  {
    id: 'ky-lowkick', name: 'Low Kick', input: '3', category: 'strike',
    startup: 11, active: 2, recovery: 14,           // blk -9, hit 0
    level: 'low', damage: 10, hitstun: 15, blockstun: 6,
    pushbackHit: 0.10, pushbackBlock: 0.18,
    hitsAir: false,
    cancelWindow: [13, 27], cancels: ['ky-shinsplit'], delayableUntil: 8,
    hitboxes: [hb(12, 13, 'footR', 0.24, 0.24, 0.66, 0.20, 0.10, { tracking: 0.55, priority: 2 })],
    counterHit: { damageMul: 1.22, hitstunBonus: 7, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 1.0, cost: 0.09, minScale: 0.22 },
    meterGain: { hit: 4, block: 2, whiff: 1 },
    anim: 'ky.lowkick',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['low', 'string-starter', 'poke'],
    aiHints: { range: 0.76, risk: 0.20, use: ['pressure', 'poke', 'mixup'] },
  },
  {
    id: 'ky-shinsplit', name: 'Low Kick, Shin Split', input: '4', category: 'strike',
    followUpOf: 'ky-lowkick',
    startup: 15, active: 3, recovery: 21,           // blk -14, hit +3
    level: 'mid', damage: 18, hitstun: 26, blockstun: 9,
    pushbackHit: 0.24, pushbackBlock: 0.32,
    hitboxes: [hb(16, 18, 'footL', 0.28, 1.06, 0.94, 1.22, 0.13, { tracking: 0.6, priority: 3 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.85, cost: 0.12, minScale: 0.22 },
    anim: 'ky.shinsplit',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['string-ender', 'delayable'],
    aiHints: { range: 1.07, risk: 0.55, use: ['pressure'] },
  },
  {
    id: 'ky-shinkick', name: 'Shin Cut', input: 'db+3', category: 'strike',
    startup: 13, active: 2, recovery: 16,           // blk -10, hit -1
    level: 'low', damage: 12, hitstun: 16, blockstun: 7,
    pushbackHit: 0.12, pushbackBlock: 0.20,
    hitsAir: false,
    hitboxes: [hb(14, 15, 'footL', 0.22, 0.18, 0.72, 0.14, 0.11, { tracking: 0.5, priority: 2 })],
    counterHit: { damageMul: 1.25, hitstunBonus: 8, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.95, cost: 0.10, minScale: 0.22 },
    anim: 'ky.shinkick',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['low', 'poke'],
    aiHints: { range: 0.83, risk: 0.3, use: ['poke', 'mixup'] },
  },
  {
    id: 'ky-sweep', name: 'Reaping Sweep', input: 'd+3', category: 'strike',
    startup: 16, active: 3, recovery: 24,           // blk -18, hit -6
    level: 'low', damage: 17, hitstun: 20, blockstun: 8,
    pushbackHit: 0.20, pushbackBlock: 0.30,
    hitsAir: false,
    hitboxes: [hb(17, 19, 'footR', 0.16, 0.14, 0.92, 0.12, 0.13, { tracking: 0.75, priority: 3 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: '' },
    onHit: { knockdown: 'soft' },
    scaling: { starterValue: 0.88, cost: 0.12, minScale: 0.22 },
    tracking: 0.75, anim: 'ky.sweep',
    sfx: { swing: 'swing-whoosh', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['low', 'knockdown', 'oki'],
    aiHints: { range: 1.05, risk: 0.7, use: ['mixup', 'oki'] },
  },
  {
    id: 'ky-crouchjab', name: 'Crouch Jab', input: 'd+1', category: 'strike',
    startup: 11, active: 2, recovery: 11,           // blk -3, hit +5
    level: 'special-mid', damage: 9, hitstun: 17, blockstun: 9,
    pushbackHit: 0.10, pushbackBlock: 0.16,
    hitboxes: [hb(12, 13, 'handR', 0.28, 0.94, 0.62, 0.96, 0.10, { tracking: 0.6, priority: 2 })],
    counterHit: { damageMul: 1.2, hitstunBonus: 6, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 1.0, cost: 0.08, minScale: 0.22 },
    anim: 'ky.crouchjab',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['crouch', 'special-mid', 'anti-crouch'],
    aiHints: { range: 0.72, risk: 0.12, use: ['pressure', 'interrupt'] },
  },
  {
    id: 'ky-crouchpunch', name: 'Rising Knuckle', input: 'd+2', category: 'strike',
    startup: 15, active: 3, recovery: 22,           // blk -16, hit +5
    level: 'special-mid', damage: 17, hitstun: 29, blockstun: 8,
    pushbackHit: 0.14, pushbackBlock: 0.28,
    hitboxes: [hb(16, 18, 'handR', 0.26, 0.92, 0.60, 1.44, 0.12, { tracking: 0.55, priority: 3 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    onHit: { launch: 7.6, juggleGravityMul: 0.94 },
    scaling: { starterValue: 0.78, cost: 0.12, minScale: 0.22 },
    anim: 'ky.crouchpunch',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-thin' },
    tags: ['launcher', 'crouch', 'special-mid', 'anti-crouch'],
    aiHints: { range: 0.76, risk: 0.68, use: ['launch', 'punish'] },
  },
  {
    id: 'ky-heeldrop', name: 'Heel Drop', input: 'd+4', category: 'strike',
    startup: 18, active: 3, recovery: 21,           // blk -14, hit +1
    level: 'mid', damage: 21, hitstun: 24, blockstun: 10,
    pushbackHit: 0.18, pushbackBlock: 0.30,
    hitsGrounded: true,
    hitboxes: [hb(19, 21, 'footR', 0.42, 0.98, 0.52, 0.10, 0.13, { tracking: 0.6, priority: 3 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: '' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.84, cost: 0.13, minScale: 0.22 },
    anim: 'ky.heeldrop',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['oki', 'knockdown', 'hits-grounded'],
    aiHints: { range: 0.78, risk: 0.6, use: ['oki', 'meaty'] },
  },
  {
    id: 'ky-knee', name: 'Trap Knee', input: 'df+3', category: 'strike',
    startup: 14, active: 2, recovery: 17,           // blk -10, hit +5
    level: 'mid', damage: 16, hitstun: 23, blockstun: 8,
    pushbackHit: 0.10, pushbackBlock: 0.22, selfPushbackBlock: 0.04,
    moveForward: 0.30,
    hitboxes: [hb(15, 16, 'kneeR', 0.20, 1.10, 0.52, 1.18, 0.13, { tracking: 0.8, priority: 3 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.9, cost: 0.11, minScale: 0.22 },
    tracking: 0.8, anim: 'ky.knee',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: '' },
    tags: ['counter-starter', 'anti-sidestep'],
    aiHints: { range: 0.70, risk: 0.35, use: ['pressure', 'interrupt'] },
  },

  // ===========================================================================
  // LAUNCHERS AND JUGGLE EXTENDERS
  // ===========================================================================
  {
    id: 'ky-hook', name: 'Rising Hook', input: 'f+2', category: 'strike',
    startup: 15, active: 3, recovery: 22,           // blk -16, hit +2
    level: 'mid', damage: 18, hitstun: 26, blockstun: 8,
    pushbackHit: 0.10, pushbackBlock: 0.34,
    hitboxes: [hb(16, 18, 'handL', 0.24, 1.16, 0.62, 1.62, 0.12, { tracking: 0.6, priority: 4 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 12, forcesState: '' },
    onHit: { launch: 8.4, juggleGravityMul: 0.9 },
    scaling: { starterValue: 0.76, cost: 0.11, minScale: 0.22 },
    anim: 'ky.hook',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['launcher', 'combo-starter'],
    aiHints: { range: 0.82, risk: 0.72, use: ['launch', 'punish'] },
  },
  {
    id: 'ky-juggle-kick', name: 'Skyline Kick', input: 'u+4', category: 'strike',
    startup: 12, active: 3, recovery: 20,           // blk -14 (rarely blockable), hit -1
    level: 'mid', damage: 13, hitstun: 21, blockstun: 8,
    pushbackHit: 0.08, pushbackBlock: 0.24,
    juggleOnly: true,
    hitboxes: [hb(13, 15, 'footL', 0.28, 1.34, 0.92, 1.66, 0.14, { tracking: 0.45, priority: 3 })],
    onHit: { juggleGravityMul: 0.82 },
    scaling: { starterValue: 0.7, cost: 0.10, minScale: 0.22 },
    anim: 'ky.jugglekick',
    sfx: { swing: 'swing-whoosh', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['juggle', 'extender'],
    aiHints: { range: 1.06, risk: 0.5, use: ['launch'] },
  },
  {
    id: 'ky-juggle-lift', name: 'Under Lift', input: 'df+1', category: 'strike',
    startup: 11, active: 2, recovery: 18,           // blk -13, hit -1
    level: 'mid', damage: 11, hitstun: 20, blockstun: 8,
    pushbackHit: 0.06, pushbackBlock: 0.22,
    juggleOnly: true,
    hitboxes: [hb(12, 13, 'handR', 0.22, 1.20, 0.58, 1.58, 0.12, { tracking: 0.5, priority: 3 })],
    onHit: { juggleGravityMul: 0.74 },
    scaling: { starterValue: 0.7, cost: 0.08, minScale: 0.22 },
    anim: 'ky.jugglelift',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: 'arc-thin' },
    tags: ['juggle', 'extender'],
    aiHints: { range: 0.70, risk: 0.45, use: ['launch'] },
  },
  {
    id: 'ky-juggle-ender', name: 'Falling Axe', input: 'df+4', category: 'strike',
    startup: 16, active: 3, recovery: 24,           // blk -18, hit -4
    level: 'mid', damage: 22, hitstun: 22, blockstun: 8,
    pushbackHit: 0.30, pushbackBlock: 0.34,
    hitsGrounded: true,
    hitboxes: [hb(17, 19, 'footR', 0.36, 1.56, 0.58, 0.42, 0.14, { tracking: 0.5, priority: 4 })],
    onHit: { knockdown: 'hard', wallSplat: true, juggleGravityMul: 1.6 },
    counterHit: { damageMul: 1.25, hitstunBonus: 8, forcesState: '' },
    scaling: { starterValue: 0.72, cost: 0.15, minScale: 0.22 },
    anim: 'ky.juggleender',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['juggle', 'ender', 'wall', 'knockdown'],
    aiHints: { range: 0.94, risk: 0.78, use: ['launch', 'oki'] },
  },

  // ===========================================================================
  // DASH AND MOVEMENT ATTACKS
  // ===========================================================================
  {
    id: 'ky-dash-elbow', name: 'Closing Elbow', input: 'ff+2', category: 'strike',
    startup: 15, active: 3, recovery: 19,           // blk -13, hit +4
    level: 'mid', damage: 20, hitstun: 26, blockstun: 9,
    pushbackHit: 0.16, pushbackBlock: 0.30,
    moveForward: 0.65,
    hitboxes: [hb(16, 18, 'elbowR', 0.24, 1.30, 0.66, 1.34, 0.14, { tracking: 0.7, priority: 3 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.86, cost: 0.12, minScale: 0.22 },
    tracking: 0.7, anim: 'ky.dashelbow',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['approach', 'wall', 'counter-starter'],
    aiHints: { range: 1.30, risk: 0.55, use: ['pressure', 'wall'] },
  },
  {
    id: 'ky-dash-low', name: 'Sliding Cut', input: 'ff+3', category: 'strike',
    startup: 17, active: 3, recovery: 25,           // blk -19, hit -8
    level: 'low', damage: 16, hitstun: 19, blockstun: 7,
    pushbackHit: 0.18, pushbackBlock: 0.26,
    moveForward: 0.95, hitsAir: false,
    hitboxes: [hb(18, 20, 'footR', 0.20, 0.16, 0.86, 0.14, 0.13, { tracking: 0.6, priority: 3 })],
    onHit: { knockdown: 'soft' },
    counterHit: { damageMul: 1.3, hitstunBonus: 10, forcesState: '' },
    scaling: { starterValue: 0.88, cost: 0.12, minScale: 0.22 },
    anim: 'ky.dashlow',
    sfx: { swing: 'swing-whoosh', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: '' },
    tags: ['low', 'approach', 'knockdown'],
    aiHints: { range: 1.45, risk: 0.8, use: ['mixup', 'range'] },
  },
  {
    id: 'ky-veinsplit', name: 'Veinsplit', input: 'qcf+2', category: 'special',
    startup: 14, active: 3, recovery: 26,           // blk -20, hit +3
    level: 'mid', damage: 26, hitstun: 31, blockstun: 9,
    pushbackHit: 0.28, pushbackBlock: 0.40,
    moveForward: 0.55,
    hitboxes: [hb(15, 17, 'handR', 0.22, 1.34, 1.02, 1.30, 0.13, { tracking: 0.8, priority: 4 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.8, cost: 0.13, minScale: 0.22 },
    meterGain: { hit: 10, block: 3, whiff: 2 },
    tracking: 0.8, anim: 'ky.veinsplit',
    sfx: { swing: 'swing-whoosh', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['special', 'whiff-punish', 'wall'],
    aiHints: { range: 1.28, risk: 0.85, use: ['whiff-punish', 'punish'] },
  },
  {
    id: 'ky-readcut', name: 'Readcut', input: 'qcb+2', category: 'special',
    startup: 11, active: 3, recovery: 24,           // blk -20, hit +8
    level: 'mid', damage: 32, hitstun: 34, blockstun: 8,
    pushbackHit: 0.20, pushbackBlock: 0.42,
    moveForward: 0.70, meterCost: 40,
    hitboxes: [hb(12, 14, 'handL', 0.20, 1.30, 1.14, 1.36, 0.14, { tracking: 0.9, priority: 5 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 18, forcesState: 'hitstun-stand' },
    onHit: { launch: 7.8, wallSplat: true, juggleGravityMul: 0.88 },
    invuln: { from: 1, to: 8, levels: ['high'] },
    scaling: { starterValue: 0.7, cost: 0.12, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    tracking: 0.9, anim: 'ky.readcut',
    sfx: { swing: 'swing-whoosh', hit: 'impact-counter', block: 'guard-heavy' },
    fx: { hit: 'spark-counter', trail: 'arc-heavy' },
    tags: ['special', 'meter', 'launcher', 'whiff-punish', 'high-crush'],
    aiHints: { range: 1.42, risk: 0.9, use: ['whiff-punish', 'launch', 'punish'] },
  },
  {
    id: 'ky-riser', name: 'Kestrel Riser', input: 'dp+2', category: 'special',
    startup: 9, active: 4, recovery: 30,            // blk -29, hit -8
    level: 'mid', damage: 24, hitstun: 25, blockstun: 8,
    pushbackHit: 0.14, pushbackBlock: 0.44,
    moveUp: 6.2, airborneAttacker: false,
    hitboxes: [hb(10, 13, 'handR', 0.18, 1.10, 0.52, 1.86, 0.15, { tracking: 0.55, priority: 5 })],
    counterHit: { damageMul: 1.35, hitstunBonus: 10, forcesState: '' },
    onHit: { launch: 8.8, juggleGravityMul: 0.9 },
    invuln: { from: 1, to: 6, levels: ['high', 'mid'] },
    scaling: { starterValue: 0.68, cost: 0.14, minScale: 0.22 },
    anim: 'ky.riser',
    sfx: { swing: 'swing-heavy', hit: 'impact-heavy', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-heavy' },
    tags: ['special', 'launcher', 'anti-air', 'reversal', 'unsafe'],
    aiHints: { range: 0.80, risk: 0.95, use: ['anti-air', 'panic', 'launch'] },
  },

  // ===========================================================================
  // FLOW STANCE AND STANCE-ONLY MOVES
  // ===========================================================================
  {
    id: 'ky-flow', name: 'Flow', input: '5', category: 'stance',
    startup: 9, active: 1, recovery: 4,
    level: 'mid', damage: 0, hitstun: 0, blockstun: 0,
    stanceEnter: 'flow',
    hitboxes: [],
    scaling: { starterValue: 1.0, cost: 0, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    anim: 'ky.flow',
    sfx: { swing: 'cloth', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['stance'],
    aiHints: { range: 0, risk: 0.25, use: ['pressure'] },
  },
  {
    id: 'ky-parry', name: 'Read Parry', input: 'b+5', category: 'defence',
    startup: 2, active: 1, recovery: 16,
    level: 'mid', damage: 0, hitstun: 0, blockstun: 0,
    hitboxes: [],
    scaling: { starterValue: 1.0, cost: 0, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    anim: 'ky.parry',
    sfx: { swing: 'cloth', hit: 'parry', block: 'guard-light' },
    fx: { hit: 'parry-ring', trail: '' },
    tags: ['parry', 'defence', 'read'],
    aiHints: { range: 0.6, risk: 0.75, use: ['panic', 'interrupt'] },
  },
  {
    id: 'ky-flow-check', name: 'Flow Check', input: '1', category: 'strike',
    stanceRequired: 'flow',
    startup: 7, active: 2, recovery: 7,             // blk +1, hit +7
    level: 'mid', damage: 7, hitstun: 15, blockstun: 9,
    pushbackHit: 0.06, pushbackBlock: 0.10, selfPushbackBlock: 0.02,
    cancelWindow: [8, 16], cancels: ['ky-flow-lace', 'ky-flow-rip'], delayableUntil: 5,
    hitboxes: [hb(8, 9, 'handL', 0.24, 1.40, 0.55, 1.39, 0.09, { tracking: 0.65, priority: 2 })],
    counterHit: { damageMul: 1.25, hitstunBonus: 7, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 1.0, cost: 0.07, minScale: 0.22 },
    meterGain: { hit: 3, block: 1, whiff: 0 },
    tracking: 0.65, anim: 'ky.flowcheck',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: 'arc-thin' },
    tags: ['flow', 'jab', 'plus-on-block', 'string-starter'],
    aiHints: { range: 0.55, risk: 0.05, use: ['pressure', 'interrupt'] },
  },
  {
    id: 'ky-flow-lace', name: 'Flow Check, Lace', input: '2', category: 'strike',
    followUpOf: 'ky-flow-check', stanceRequired: 'flow',
    startup: 10, active: 2, recovery: 15,           // blk -7, hit +6
    level: 'mid', damage: 14, hitstun: 22, blockstun: 9,
    pushbackHit: 0.14, pushbackBlock: 0.24,
    hitboxes: [hb(11, 12, 'handR', 0.28, 1.34, 0.74, 1.36, 0.11, { tracking: 0.6, priority: 3 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 12, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.9, cost: 0.10, minScale: 0.22 },
    anim: 'ky.flowlace',
    sfx: { swing: 'swing-light', hit: 'impact-medium', block: 'guard-light' },
    fx: { hit: 'spark-medium', trail: 'arc-thin' },
    tags: ['flow', 'string', 'delayable'],
    aiHints: { range: 0.85, risk: 0.30, use: ['pressure'] },
  },
  {
    id: 'ky-flow-rip', name: 'Flow Check, Rip', input: '3', category: 'strike',
    followUpOf: 'ky-flow-check', stanceRequired: 'flow',
    startup: 12, active: 2, recovery: 18,           // blk -12, hit -3
    level: 'low', damage: 13, hitstun: 16, blockstun: 7,
    pushbackHit: 0.12, pushbackBlock: 0.22,
    hitsAir: false,
    hitboxes: [hb(13, 14, 'footL', 0.22, 0.18, 0.72, 0.15, 0.11, { tracking: 0.55, priority: 2 })],
    counterHit: { damageMul: 1.3, hitstunBonus: 9, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 0.9, cost: 0.10, minScale: 0.22 },
    anim: 'ky.flowrip',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['flow', 'low', 'mixup', 'string-ender'],
    aiHints: { range: 0.83, risk: 0.45, use: ['mixup', 'pressure'] },
  },
  {
    id: 'ky-flow-lash', name: 'Flow Lash', input: '3', category: 'strike',
    stanceRequired: 'flow',
    startup: 9, active: 2, recovery: 13,            // blk -7, hit +2
    level: 'low', damage: 9, hitstun: 16, blockstun: 8,
    pushbackHit: 0.08, pushbackBlock: 0.16,
    hitsAir: false,
    hitboxes: [hb(10, 11, 'footR', 0.20, 0.20, 0.62, 0.17, 0.10, { tracking: 0.6, priority: 2 })],
    counterHit: { damageMul: 1.28, hitstunBonus: 8, forcesState: 'hitstun-crouch' },
    scaling: { starterValue: 1.0, cost: 0.08, minScale: 0.22 },
    meterGain: { hit: 3, block: 1, whiff: 0 },
    anim: 'ky.flowlash',
    sfx: { swing: 'swing-light', hit: 'impact-body', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['flow', 'low', 'fast'],
    aiHints: { range: 0.62, risk: 0.18, use: ['pressure', 'mixup'] },
  },
  {
    id: 'ky-flow-hook', name: 'Flow Hook', input: 'f+2', category: 'strike',
    stanceRequired: 'flow',
    startup: 13, active: 3, recovery: 21,           // blk -15, hit +4
    level: 'mid', damage: 19, hitstun: 27, blockstun: 8,
    pushbackHit: 0.10, pushbackBlock: 0.32,
    hitboxes: [hb(14, 16, 'handL', 0.22, 1.14, 0.64, 1.66, 0.12, { tracking: 0.65, priority: 4 })],
    counterHit: { damageMul: 1.4, hitstunBonus: 14, forcesState: '' },
    onHit: { launch: 8.6, juggleGravityMul: 0.88 },
    scaling: { starterValue: 0.74, cost: 0.11, minScale: 0.22 },
    anim: 'ky.flowhook',
    sfx: { swing: 'swing-heavy', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-heavy', trail: 'arc-wide' },
    tags: ['flow', 'launcher'],
    aiHints: { range: 0.86, risk: 0.70, use: ['launch', 'punish'] },
  },
  {
    id: 'ky-flow-pivot', name: 'Flow Pivot', input: 'b+3', category: 'strike',
    stanceRequired: 'flow',
    startup: 15, active: 3, recovery: 19,           // blk -13, hit +6
    level: 'mid', damage: 18, hitstun: 27, blockstun: 9,
    pushbackHit: 0.20, pushbackBlock: 0.30,
    hitboxes: [hb(16, 18, 'footL', 0.18, 1.08, 0.98, 1.30, 0.13, { tracking: 1.0, priority: 4 })],
    counterHit: { damageMul: 1.45, hitstunBonus: 14, forcesState: 'hitstun-stand' },
    onHit: { wallSplat: true },
    scaling: { starterValue: 0.85, cost: 0.12, minScale: 0.22 },
    tracking: 1.0, anim: 'ky.flowpivot',
    sfx: { swing: 'swing-whoosh', hit: 'impact-medium', block: 'guard-heavy' },
    fx: { hit: 'spark-medium', trail: 'arc-wide' },
    tags: ['flow', 'anti-sidestep', 'wall'],
    aiHints: { range: 1.11, risk: 0.55, use: ['anti-sidestep', 'wall'] },
  },
  {
    id: 'ky-flow-trap', name: 'Flow Trap', input: 'df+1', category: 'strike',
    stanceRequired: 'flow',
    startup: 10, active: 2, recovery: 12,           // blk -5, hit +9
    level: 'special-mid', damage: 11, hitstun: 22, blockstun: 8,
    pushbackHit: 0.04, pushbackBlock: 0.14, selfPushbackBlock: 0.04,
    moveForward: 0.24,
    hitboxes: [hb(11, 12, 'handR', 0.20, 1.06, 0.54, 1.10, 0.11, { tracking: 0.85, priority: 3 })],
    counterHit: { damageMul: 1.5, hitstunBonus: 16, forcesState: 'hitstun-stand' },
    scaling: { starterValue: 0.95, cost: 0.09, minScale: 0.22 },
    tracking: 0.85, anim: 'ky.flowtrap',
    sfx: { swing: 'swing-light', hit: 'impact-light', block: 'guard-light' },
    fx: { hit: 'spark-light', trail: '' },
    tags: ['flow', 'special-mid', 'anti-crouch', 'counter-starter'],
    aiHints: { range: 0.65, risk: 0.20, use: ['pressure', 'interrupt', 'anti-crouch'] },
  },

  // ===========================================================================
  // THROWS
  // ===========================================================================
  {
    id: 'ky-throw', name: 'Collar Drag', input: '1+3', category: 'throw',
    startup: 11, active: 2, recovery: 34,
    damage: 30, hitstun: 0, blockstun: 14,
    throwBox: { from: 12, to: 13, ox: 0.42, oy: 1.10, oz: 0, r: 0.40, catches: 'stand' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.6, cost: 0.2, minScale: 0.22 },
    meterGain: { hit: 6, block: 0, whiff: 1 },
    anim: 'ky.throw',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['throw'],
    aiHints: { range: 0.68, risk: 0.65, use: ['mixup', 'pressure'] },
  },
  {
    id: 'ky-throw-back', name: 'Reverse Wind', input: 'b+1+3', category: 'throw',
    startup: 12, active: 2, recovery: 36,
    damage: 34, hitstun: 0, blockstun: 14,
    throwBox: { from: 13, to: 14, ox: 0.40, oy: 1.08, oz: 0, r: 0.40, catches: 'stand' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.6, cost: 0.2, minScale: 0.22 },
    meterGain: { hit: 8, block: 0, whiff: 1 },
    anim: 'ky.throwback',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['throw', 'wall-carry'],
    aiHints: { range: 0.66, risk: 0.7, use: ['mixup', 'wall'] },
  },
  {
    id: 'ky-lowthrow', name: 'Ankle Pick', input: 'qcf+1+3', category: 'command-throw',
    startup: 14, active: 2, recovery: 32,
    damage: 24, hitstun: 0, blockstun: 12,
    throwBox: { from: 15, to: 16, ox: 0.36, oy: 0.52, oz: 0, r: 0.38, catches: 'crouch' },
    onHit: { knockdown: 'hard' },
    scaling: { starterValue: 0.65, cost: 0.2, minScale: 0.22 },
    meterGain: { hit: 10, block: 0, whiff: 1 },
    anim: 'ky.lowthrow',
    sfx: { swing: 'cloth', hit: 'throw-grab', block: 'guard-light' },
    fx: { hit: 'throw-burst', trail: '' },
    tags: ['command-throw', 'anti-crouch'],
    aiHints: { range: 0.62, risk: 0.8, use: ['mixup', 'anti-crouch'] },
  },

  // ===========================================================================
  // FINISHER — telegraphed, unblockable, costs almost the whole Read bar.
  // ===========================================================================
  {
    id: 'ky-severance', name: 'Severance', input: '2+4', category: 'finisher',
    startup: 26, active: 4, recovery: 40,
    level: 'unblockable', damage: 96, hitstun: 40, blockstun: 0,
    pushbackHit: 0.4, pushbackBlock: 0,
    moveForward: 1.10, meterCost: 85,
    hitboxes: [hb(27, 30, 'handR', 0.18, 1.24, 1.16, 1.42, 0.20, { tracking: 1.0, priority: 9 })],
    counterHit: { damageMul: 1.0, hitstunBonus: 0, forcesState: '' },
    onHit: { knockdown: 'hard', wallSplat: true, hitstop: 26 },
    invuln: { from: 20, to: 30, levels: ['high', 'mid', 'low', 'throw'] },
    scaling: { starterValue: 0.5, cost: 0.3, minScale: 0.22 },
    meterGain: { hit: 0, block: 0, whiff: 0 },
    tracking: 1.0, anim: 'ky.severance',
    sfx: { swing: 'swing-whoosh', hit: 'impact-counter', block: 'guard-heavy' },
    fx: { hit: 'finisher-shock', trail: 'arc-heavy' },
    tags: ['finisher', 'unblockable', 'meter', 'cinematic'],
    aiHints: { range: 1.34, risk: 0.9, use: ['punish', 'whiff-punish'] },
  },
];

/**
 * Every animation identifier Kestrel's move data references. `src/animation` must provide a clip
 * for each; `requiredAnimations()` in moves/index.js is the runtime check.
 */
export const KESTREL_ANIMS = KESTREL_MOVES.map((m) => m.anim);
